import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDateSafe, apiErrorMessage } from '@/lib/api-helpers';
import { Button } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ouropix/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@ouropix/ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ouropix/ui';
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Calendar,
  Download,
  Edit2,
  FileDown,
  KeyRound,
  Loader2,
  Pause,
  Play,
  ShieldCheck,
  Sparkles,
  Trophy,
} from 'lucide-react';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { GAME_STATUS_LABELS, GAME_STATUS_VARIANT } from '@/lib/game-enums';
import type { AdminGame } from '@/components/games/types';
import { PrizeAllocator } from '@/components/games/PrizeAllocator';

export const Route = createFileRoute('/_admin/jogos/$id')({
  component: GameDetailPage,
});

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between py-2 border-b last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%]">{value}</span>
    </div>
  );
}

function GameDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [prizesModalOpen, setPrizesModalOpen] = useState(false);
  const [publishRevealOpen, setPublishRevealOpen] = useState(false);

  const { data: game, isLoading } = useQuery<AdminGame>({
    queryKey: ['admin-game', id],
    queryFn: () => api.get(`/games/${id}`).then(r => {
      // Gateway proto-loader serializa camelCase; admin app espera snake_case (AdminGame).
      // Backend retorna { game: {...} } no shape novo; { data: {...} } no antigo.
      const d = r.data;
      const raw = d?.game ?? d?.data ?? d;
      // Normaliza camelCase -> snake_case pros campos usados na UI
      const STATUS_NAMES: Record<string, string> = {
        GAME_STATUS_DRAFT: 'draft',
        GAME_STATUS_SCHEDULED: 'scheduled',
        GAME_STATUS_ACTIVE: 'active',
        GAME_STATUS_PAUSED: 'paused',
        GAME_STATUS_COMPLETED: 'completed',
        GAME_STATUS_CANCELLED: 'cancelled',
      };
      const statusRaw = raw.status;
      const status = typeof statusRaw === 'string' ? (STATUS_NAMES[statusRaw] ?? statusRaw.toLowerCase()) : 'draft';
      return {
        ...raw,
        status,
        play_price: Number(raw.play_price ?? raw.playPrice ?? 0),
        total_plays: Number(raw.total_plays ?? raw.totalPlays ?? 0),
        used_plays: Number(raw.used_plays ?? raw.usedPlays ?? 0),
        image_url: raw.image_url ?? raw.imageUrl ?? null,
        icon_url: raw.icon_url ?? raw.iconUrl ?? null,
        cover_url: raw.cover_url ?? raw.coverUrl ?? null,
        start_at: raw.start_at ?? raw.startAt ?? null,
        end_at: raw.end_at ?? raw.endAt ?? null,
        created_at: raw.created_at ?? raw.createdAt ?? '',
        updated_at: raw.updated_at ?? raw.updatedAt ?? '',
        pool_generated: raw.pool_generated ?? raw.poolGenerated ?? false,
        has_final_draw: raw.has_final_draw ?? raw.hasFinalDraw ?? false,
        draw_at: raw.draw_at ?? raw.drawAt ?? null,
        draw_status: raw.draw_status ?? raw.drawStatus ?? null,
      } as AdminGame;
    }),
  });

  const { data: prizes, isPending: loadingPrizes } = useQuery({
    queryKey: ['game-prizes', id],
    queryFn: () => api.get(`/admin/games/${id}/prizes`).then(r => {
      const d = r.data;
      const arr = d?.prizes ?? d?.data ?? d ?? [];
      return Array.isArray(arr) ? arr : [];
    }),
  });

  // INFRA-017: track async job ID quando pool generation T3+ dispatcha pra Lambda
  const [poolJobId, setPoolJobId] = useState<string | null>(null);

  const generatePool = useMutation({
    mutationFn: (force: boolean) =>
      api.post(`/admin/games/${id}/generate-pool`, { force_regenerate: force }).then(r => r.data),
    onSuccess: (data: { status?: 'completed' | 'generating'; job_id?: string; estimated_duration_ms?: number }) => {
      // INFRA-017: T3+ retorna { status: 'generating', job_id }
      if (data.status === 'generating' && data.job_id) {
        setPoolJobId(data.job_id);
        const eta = data.estimated_duration_ms ? Math.ceil(data.estimated_duration_ms / 1000) : null;
        toast.info(
          `Geracao iniciada (job ${data.job_id.slice(0, 8)}...).${eta ? ` Estimado: ~${eta}s` : ''}`,
        );
      } else {
        // Sincrono (T0-T2)
        queryClient.invalidateQueries({ queryKey: ['admin-game', id] });
        queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] });
        toast.success('Pool gerado com sucesso');
      }
    },
    onError: (e: unknown) => {
      const raw =
        (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message ??
        (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : '') ?? '';
      // pool-svc rejeita quando SUM(premios) != total_plays
      const msg = /does not match|SUM\(game_prizes/i.test(raw)
        ? 'A soma das quantidades de prêmios precisa ser exatamente igual ao total de números do jogo. Aloque um prêmio "Sem Prêmio" para cobrir os números restantes.'
        : (raw || 'Erro ao gerar pool');
      toast.error(msg);
    },
  });

  // INFRA-017: polling do status do job async (a cada 2s ate terminal state)
  const { data: poolJob } = useQuery<{
    job_id: string;
    status: 'queued' | 'running' | 'completed' | 'failed';
    progress_percent: number;
    plays_inserted: number;
    total_plays: number;
    error_message: string;
    started_at: string;
    completed_at: string;
  } | null>({
    queryKey: ['pool-job', poolJobId],
    queryFn: () =>
      poolJobId
        ? api.get(`/pool/jobs/${poolJobId}`).then(r => r.data ?? null)
        : Promise.resolve(null),
    enabled: !!poolJobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Polling a cada 2s enquanto queued/running. Para de fazer polling em terminal.
      return status === 'queued' || status === 'running' ? 2000 : false;
    },
  });

  // Quando job termina, refresh game state + clear polling
  useEffect(() => {
    if (!poolJob) return;
    if (poolJob.status === 'completed') {
      toast.success(`Pool gerado: ${poolJob.plays_inserted.toLocaleString('pt-BR')} jogadas`);
      queryClient.invalidateQueries({ queryKey: ['admin-game', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] });
      setPoolJobId(null);
    } else if (poolJob.status === 'failed') {
      toast.error(`Falha na geracao: ${poolJob.error_message || 'erro desconhecido'}`);
      setPoolJobId(null);
    }
  }, [poolJob, queryClient, id]);

  // INFRA-015: provably-fair commit-reveal — busca estado do reveal pra
  // exibir status + permitir publicacao apos game.status='completed'.
  // Endpoint publico: /api/pool/public/:gameId/verify retorna sempre o
  // commitment + commitmentPublishedAt; seedRevealed/preShuffleItems
  // somente quando reveal_published=true.
  const { data: poolVerify } = useQuery<{
    seedCommitment?: string;
    seedAlgorithm?: string;
    commitmentPublishedAt?: string | null;
    seedRevealed?: string | null;
    seedRevealedAt?: string | null;
    revealStatus?: 'pending' | 'revealed' | 'unavailable';
  }>({
    queryKey: ['pool-verify', id],
    queryFn: () => api.get(`/pool/public/${id}/verify`).then(r => r.data ?? {}),
    // Reveal nao muda durante a sessao a menos que admin acione publish.
    refetchInterval: false,
    refetchOnWindowFocus: false,
    // 404 quando pool ainda nao gerada — silenciar pra nao poluir UI
    retry: false,
  });

  const publishReveal = useMutation({
    mutationFn: () => api.post(`/pool/admin/${id}/publish-reveal`).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pool-verify', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-game', id] });
      toast.success('Reveal publicado. Seed e items pre-shuffle agora publicos.');
      setPublishRevealOpen(false);
    },
    onError: (e: unknown) => {
      toast.error(apiErrorMessage(e, 'Erro ao publicar reveal'));
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: 'active' | 'paused' | 'draft') =>
      api.patch(`/admin/games/${id}`, { status, reason: `Manual ${status} from admin` }).then(r => r.data),
    onSuccess: (_, status) => {
      queryClient.invalidateQueries({ queryKey: ['admin-game', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] });
      toast.success(
        status === 'active' ? 'Jogo ativado' : status === 'paused' ? 'Jogo pausado' : 'Jogo voltou pra draft'
      );
    },
    onError: (e: unknown) => {
      toast.error(apiErrorMessage(e, 'Erro ao alterar status'));
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <p className="text-muted-foreground">Jogo não encontrado.</p>
        <Button variant="outline" onClick={() => navigate({ to: '/jogos' })}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
      </div>
    );
  }

  const progress = game.total_plays > 0 ? (game.used_plays / game.total_plays) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: '/jogos' })} aria-label="Voltar">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3 flex-1">
          {game.icon_url && (
            <img src={game.icon_url} alt={game.title} className="h-10 w-10 rounded-lg object-cover" />
          )}
          <div>
            <h1 className="text-2xl font-bold">{game.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={GAME_STATUS_VARIANT[game.status]}>
                {GAME_STATUS_LABELS[game.status]}
              </Badge>
              {game.featured && <Badge variant="secondary">Destaque</Badge>}
              {game.pool_generated && <Badge variant="outline">Pool gerada</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {game.status === 'draft' && game.pool_generated && (
            <Button onClick={() => setStatus.mutate('active')} disabled={setStatus.isPending}>
              {setStatus.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Ativar
            </Button>
          )}
          {game.status === 'active' && (
            <Button variant="secondary" onClick={() => setStatus.mutate('paused')} disabled={setStatus.isPending}>
              {setStatus.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Pause className="mr-2 h-4 w-4" />}
              Pausar
            </Button>
          )}
          {game.status === 'paused' && (
            <Button onClick={() => setStatus.mutate('active')} disabled={setStatus.isPending}>
              {setStatus.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Retomar
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Informações</TabsTrigger>
          <TabsTrigger value="dashboard">
            <BarChart3 className="h-3.5 w-3.5 mr-1" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="pool">Pool</TabsTrigger>
          <TabsTrigger value="prizes">Prêmios</TabsTrigger>
          <TabsTrigger value="draw">
            <Trophy className="h-3.5 w-3.5 mr-1" /> Sorteio
          </TabsTrigger>
          <TabsTrigger value="report">
            <FileDown className="h-3.5 w-3.5 mr-1" /> Relatório
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Detalhes do Jogo</CardTitle>
            </CardHeader>
            <CardContent>
              <InfoRow label="ID" value={<code className="text-xs bg-muted px-1 rounded">{game.id}</code>} />
              <InfoRow label="Descrição" value={game.description ?? '-'} />
              <InfoRow label="Modalidade" value={game.modality?.name ?? '-'} />
              <InfoRow label="Tema" value={game.theme?.name ?? '-'} />
              <InfoRow label="Preco por jogada" value={formatCurrency(game.play_price)} />
              <InfoRow label="Total de jogadas" value={game.total_plays.toLocaleString('pt-BR')} />
              <InfoRow label="Jogadas usadas" value={game.used_plays.toLocaleString('pt-BR')} />
              <InfoRow label="Pool gerada" value={game.pool_generated ? 'Sim' : 'Nao'} />
              <InfoRow
                label="Inicio"
                value={
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDateSafe(game.start_at, 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                  </div>
                }
              />
              <InfoRow label="Encerrado em" value={formatDateSafe(game.end_at, 'dd/MM/yyyy HH:mm', { locale: ptBR })} />
              <InfoRow label="Criado em" value={formatDateSafe(game.created_at, 'dd/MM/yyyy HH:mm', { locale: ptBR })} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pool" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Informações do Pool</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Progresso</span>
                  <span className="font-medium">{progress.toFixed(1)}%</span>
                </div>
                <div className="h-3 w-full bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${progress}%` }} />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{game.used_plays.toLocaleString('pt-BR')} vendidas</span>
                  <span>{game.total_plays.toLocaleString('pt-BR')} total</span>
                </div>
              </div>
              <InfoRow label="Pool gerada" value={game.pool_generated ? 'Sim' : 'Nao'} />
              <InfoRow label="Receita atual" value={formatCurrency((game.used_plays || 0) * (game.play_price || 0))} />
              <InfoRow label="Receita potencial" value={formatCurrency((game.total_plays || 0) * (game.play_price || 0))} />

              {/* INFRA-017: progress bar do job async (T3+) */}
              {poolJob && (poolJob.status === 'queued' || poolJob.status === 'running') && (
                <div className="space-y-2 pt-4 border-t mt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span className="font-medium">
                      {poolJob.status === 'queued' ? 'Aguardando worker...' : 'Gerando pool...'}
                    </span>
                    <span className="text-muted-foreground ml-auto">
                      {poolJob.progress_percent}% · {poolJob.plays_inserted.toLocaleString('pt-BR')} / {poolJob.total_plays.toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all duration-500"
                      style={{ width: `${poolJob.progress_percent}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Geracao assincrona (T3+) via Lambda. Job ID:{' '}
                    <code className="bg-muted px-1 rounded">{poolJob.job_id.slice(0, 8)}...</code>
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t mt-4">
                <div className="text-sm text-muted-foreground">
                  {game.pool_generated
                    ? 'Pool ja foi gerada. Regerar apaga numeros atuais.'
                    : 'Gere o pool antes de ativar o jogo.'}
                </div>
                <Button
                  onClick={() => generatePool.mutate(game.pool_generated ?? false)}
                  disabled={generatePool.isPending || game.status !== 'draft' || !!poolJobId}
                  variant={game.pool_generated ? 'outline' : 'default'}
                >
                  {generatePool.isPending || poolJobId
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <Sparkles className="mr-2 h-4 w-4" />}
                  {game.pool_generated ? 'Regerar Pool' : 'Gerar Pool'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* INFRA-015: Provably-fair commit-reveal */}
          {game.pool_generated && poolVerify?.seedCommitment && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  Provably-Fair (Commit-Reveal)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <InfoRow
                  label="Commitment"
                  value={
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded break-all">
                      {poolVerify.seedCommitment}
                    </code>
                  }
                />
                <InfoRow label="Algoritmo" value={poolVerify.seedAlgorithm ?? 'sha256-chainkey-v2'} />
                <InfoRow
                  label="Commitment publicado em"
                  value={
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDateSafe(poolVerify.commitmentPublishedAt, 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                    </div>
                  }
                />
                <InfoRow
                  label="Status do reveal"
                  value={
                    poolVerify.revealStatus === 'revealed' ? (
                      <Badge variant="secondary" className="gap-1">
                        <ShieldCheck className="h-3 w-3" />
                        Publicado
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1">
                        <KeyRound className="h-3 w-3" />
                        Pendente
                      </Badge>
                    )
                  }
                />
                {poolVerify.revealStatus === 'revealed' && poolVerify.seedRevealedAt && (
                  <InfoRow
                    label="Seed revelado em"
                    value={
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDateSafe(poolVerify.seedRevealedAt, 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                      </div>
                    }
                  />
                )}

                <div className="flex items-center justify-between pt-4 border-t mt-4">
                  <div className="text-sm text-muted-foreground max-w-[60%]">
                    {poolVerify.revealStatus === 'revealed'
                      ? 'Seed e items pre-shuffle ja publicos. Auditor externo pode reproduzir o Fisher-Yates.'
                      : game.status === 'completed'
                        ? 'Jogo encerrado. Voce pode publicar o reveal — operacao irreversivel.'
                        : 'Reveal so pode ser publicado apos game.status=completed.'}
                  </div>
                  <Button
                    onClick={() => setPublishRevealOpen(true)}
                    disabled={
                      poolVerify.revealStatus === 'revealed' ||
                      game.status !== 'completed' ||
                      publishReveal.isPending
                    }
                    variant="default"
                  >
                    {publishReveal.isPending
                      ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      : <KeyRound className="mr-2 h-4 w-4" />}
                    Publicar Reveal
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="prizes" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Prêmios Vinculados</CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPrizesModalOpen(true)}
                disabled={game.status !== 'draft' || (game.pool_generated ?? false)}
              >
                <Edit2 className="mr-1 h-3 w-3" />
                {prizes?.length ? 'Editar' : 'Adicionar'} Prêmios
              </Button>
            </CardHeader>
            <CardContent>
              {loadingPrizes ? (
                <p className="text-sm text-muted-foreground py-4">Carregando prêmios...</p>
              ) : !prizes?.length ? (
                <p className="text-sm text-muted-foreground py-4">Nenhum prêmio vinculado.</p>
              ) : (
                <div className="space-y-2">
                  {(prizes as Array<{
                    id: string;
                    prize?: { name?: string; value?: number };
                    name?: string;
                    value?: number;
                    prize_name?: string;
                    prizeName?: string;
                    prize_value?: number;
                    prizeValue?: number;
                    quantity: number;
                    rarity?: string;
                  }>).map(p => (
                    <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="text-sm font-medium">
                          {p.prizeName ?? p.prize_name ?? p.prize?.name ?? p.name ?? '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Quantidade: {p.quantity}{p.rarity ? ` · Raridade: ${p.rarity}` : ''}
                        </p>
                      </div>
                      <span className="text-sm font-medium">
                        {formatCurrency(Number(p.prizeValue ?? p.prize_value ?? p.prize?.value ?? p.value ?? 0))}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dashboard" className="mt-4">
          <DashboardTab game={game} />
        </TabsContent>

        <TabsContent value="draw" className="mt-4">
          <DrawTab game={game} />
        </TabsContent>

        <TabsContent value="report" className="mt-4">
          <ReportTab game={game} />
        </TabsContent>
      </Tabs>

      <PrizeAllocator
        open={prizesModalOpen}
        onOpenChange={setPrizesModalOpen}
        gameId={id}
        gameTotalPlays={game.total_plays}
        gameStatus={game.status}
        poolGenerated={game.pool_generated ?? false}
        initialPrizes={(prizes ?? []) as never}
      />

      {/* INFRA-015: Confirma publicacao do reveal — operacao irreversivel */}
      <AlertDialog open={publishRevealOpen} onOpenChange={setPublishRevealOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Publicar Reveal — Operacao Irreversivel
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Voce esta prestes a publicar o <strong>master seed</strong> deste pool.
                  Apos esta acao:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>O seed ficara publicamente acessivel via <code className="bg-muted px-1 rounded">/verificar-pool/{id}</code></li>
                  <li>Os <em>items pre-shuffle</em> tambem ficarao publicos</li>
                  <li>Qualquer auditor externo podera reproduzir o Fisher-Yates e validar o sorteio</li>
                  <li>Esta acao nao pode ser revertida</li>
                </ul>
                <p className="pt-2 text-muted-foreground">
                  Confirme apenas se o jogo esta encerrado e voce tem certeza.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishReveal.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                publishReveal.mutate();
              }}
              disabled={publishReveal.isPending}
            >
              {publishReveal.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Publicar Reveal
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================
// DashboardTab — KPIs do jogo
// ============================================================
function DashboardTab({ game }: { game: AdminGame }) {
  const revenue = (game.used_plays || 0) * (game.play_price || 0);
  const potential = (game.total_plays || 0) * (game.play_price || 0);
  const ticketAvg = game.used_plays > 0 ? revenue / game.used_plays : 0;
  const remaining = (game.total_plays || 0) - (game.used_plays || 0);
  const sellThrough = game.total_plays > 0 ? (game.used_plays / game.total_plays) * 100 : 0;
  const g = game as AdminGame & { has_final_draw?: boolean; draw_at?: string | null };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <KpiCard label="Receita Atual" value={formatCurrency(revenue)} hint={`${sellThrough.toFixed(1)}% vendido`} />
      <KpiCard label="Receita Potencial" value={formatCurrency(potential)} hint="Se vender tudo" />
      <KpiCard label="Ticket Médio" value={formatCurrency(ticketAvg)} hint="Por jogada vendida" />
      <KpiCard label="Restante" value={remaining.toLocaleString('pt-BR')} hint={`de ${(game.total_plays || 0).toLocaleString('pt-BR')}`} />
      <KpiCard label="Status" value={GAME_STATUS_LABELS[game.status] ?? game.status} hint={game.featured ? 'Destaque' : 'Normal'} />
      <KpiCard label="Pool Gerada" value={game.pool_generated ? 'Sim' : 'Não'} hint={game.pool_generated ? 'Pronto pra ativar' : 'Pendente'} />
      <KpiCard
        label="Sorteio Final"
        value={g.has_final_draw ? 'Sim' : 'Não'}
        hint={formatDateSafe(g.draw_at, 'dd/MM HH:mm', { locale: ptBR })}
      />
      <KpiCard
        label="Encerramento"
        value={formatDateSafe(game.end_at, 'dd/MM/yyyy', { locale: ptBR })}
        hint="Data programada"
      />
    </div>
  );
}

function KpiCard({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
}

// ============================================================
// DrawTab — Sorteio final
// ============================================================
function DrawTab({ game }: { game: AdminGame }) {
  const g = game as AdminGame & { has_final_draw?: boolean; draw_at?: string | null; draw_status?: string };

  if (!g.has_final_draw) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          <Trophy className="h-8 w-8 mx-auto mb-2 opacity-50" />
          Este jogo não possui sorteio final configurado.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Sorteio Final
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <InfoRow label="Status" value={g.draw_status ?? 'pending'} />
          <InfoRow
            label="Data agendada"
            value={formatDateSafe(g.draw_at, 'dd/MM/yyyy HH:mm', { locale: ptBR })}
          />
          <InfoRow label="Premiação configurada" value="ver tab Prêmios" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Auditoria do Sorteio</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            A trilha de auditoria do sorteio é exibida automaticamente após a execução.
            Eventos LOTEP-compliance aparecem em ordem cronológica.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// ReportTab — Export CSV
// ============================================================
function ReportTab({ game }: { game: AdminGame }) {
  const exportRevenue = async () => {
    try {
      toast.loading('Gerando relatório de receita...', { id: 'export-revenue' });
      const res = await api.get('/admin/financial/revenue/export', {
        params: { game_id: game.id, format: 'csv' },
        responseType: 'blob',
      });
      downloadBlob(res.data, `receita-${game.id.slice(0, 8)}.csv`);
      toast.success('Relatório de receita exportado.', { id: 'export-revenue' });
    } catch {
      toast.error('Falha ao exportar receita.', { id: 'export-revenue' });
    }
  };

  const exportTransactions = async () => {
    try {
      toast.loading('Gerando relatório de transações...', { id: 'export-tx' });
      const res = await api.get('/admin/transactions', {
        params: { game_id: game.id, format: 'csv', limit: 1000 },
        responseType: 'blob',
      });
      downloadBlob(res.data, `transacoes-${game.id.slice(0, 8)}.csv`);
      toast.success('Relatório de transações exportado.', { id: 'export-tx' });
    } catch {
      toast.error('Falha ao exportar transações.', { id: 'export-tx' });
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Relatório de Receita</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            CSV com receita bruta, taxas aplicadas e líquido por período.
          </p>
          <Button variant="outline" className="w-full" onClick={exportRevenue}>
            <Download className="h-4 w-4 mr-2" />
            Exportar Receita
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Relatório de Transações</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            CSV com todas as compras e prêmios distribuídos pelo jogo.
          </p>
          <Button variant="outline" className="w-full" onClick={exportTransactions}>
            <Download className="h-4 w-4 mr-2" />
            Exportar Transações
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function downloadBlob(data: unknown, filename: string) {
  const blob = data instanceof Blob ? data : new Blob([JSON.stringify(data)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
